import {
  Alert,
  Avatar,
  Box,
  Button,
  CloseButton,
  Group,
  List,
  Loader,
  Paper,
  Popover,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  TypographyStylesProvider,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import moment from 'moment';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { ItemAiChatAnswer, ItemAiChatMessage } from '@e-origin/contracts';
import { EORIGIN_DATE_FORMAT_WITH_TIME } from '@e-origin/shared';

import LogoMark from '@assets/images/logo192.png';
import { itemAiChatErrorMessage, useOpenItemAiChat, useSendItemAiChatMessage } from '@hooks/declaration';

const MAX_QUESTION_LENGTH = 2000;

interface LocalQuestion {
  key: number;
  question: string;
  failed: boolean;
}

const OUTCOME_NOTICES: Record<Exclude<ItemAiChatAnswer['outcome'], 'answer'>, { title: string; color: string }> = {
  clarification: { title: 'Clarification needed', color: 'blue' },
  unsupported: { title: 'Not supported', color: 'gray' },
  withheld: { title: 'Answer withheld', color: 'yellow' },
};

const AnswerBody: React.FC<{ answer: ItemAiChatAnswer }> = ({ answer }) => {
  const markdown = (
    <TypographyStylesProvider fz="sm">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer.text}</ReactMarkdown>
    </TypographyStylesProvider>
  );

  return (
    <Stack gap="xs">
      {answer.outcome === 'answer' ? (
        markdown
      ) : (
        <Alert variant="light" color={OUTCOME_NOTICES[answer.outcome].color} title={OUTCOME_NOTICES[answer.outcome].title} p="sm">
          {markdown}
        </Alert>
      )}
      {answer.caveats.length > 0 && (
        <List size="xs" c="dimmed" data-pw="ai-chat-caveats">
          {answer.caveats.map((caveat, index) => (
            <List.Item key={index}>{caveat}</List.Item>
          ))}
        </List>
      )}
    </Stack>
  );
};

const authorLabel = (message: ItemAiChatMessage): string => {
  if (message.isCurrentUser) return 'You';
  return message.authorName || 'a colleague';
};

const QuestionBubble: React.FC<{ question: string; author: string; createdAt?: string; own: boolean; failed?: boolean }> = ({
  question,
  author,
  createdAt,
  own,
  failed,
}) => (
  <Box style={{ alignSelf: 'flex-end', maxWidth: '80%' }} data-pw="ai-chat-message-user" data-own={own}>
    <Text size="xs" c="dimmed" ta="right" mb={2} data-pw="ai-chat-message-author">
      {author}
      {createdAt && ` · ${moment(createdAt).format(EORIGIN_DATE_FORMAT_WITH_TIME)}`}
    </Text>
    <Paper p="sm" radius="md" bg={own ? 'blue.0' : 'gray.1'}>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {question}
      </Text>
    </Paper>
    {failed && (
      <Text size="xs" c="red" ta="right" mt={2}>
        Not answered
      </Text>
    )}
  </Box>
);

const AnswerBubble: React.FC<{ answer: ItemAiChatAnswer }> = ({ answer }) => (
  <Box style={{ maxWidth: '95%' }} data-pw="ai-chat-message-assistant" data-outcome={answer.outcome}>
    <Group gap={6} mb={4} data-pw="ai-chat-agent-name">
      <Avatar src={LogoMark} alt="e-origin" size={22} radius="xl" />
      <Text size="xs" fw={600}>
        Risk Analysis Agent
      </Text>
    </Group>
    <Paper p="sm" radius="md" withBorder>
      <AnswerBody answer={answer} />
    </Paper>
  </Box>
);

interface RiskAnalysisChatBodyProps {
  declarationId: string;
  goodsShipmentItemId: string;
}

const useRiskAnalysisChat = ({ declarationId, goodsShipmentItemId, opened }: RiskAnalysisChatBodyProps & { opened: boolean }) => {
  const [sessionId, setSessionId] = useState<string>();
  const [history, setHistory] = useState<ItemAiChatMessage[]>([]);
  const [localQuestions, setLocalQuestions] = useState<LocalQuestion[]>([]);
  const nextKey = useRef(0);

  const openChat = useOpenItemAiChat();
  const sendMessage = useSendItemAiChatMessage();

  useEffect(() => {
    if (!opened) return;
    openChat.mutate(
      { declarationId, goodsShipmentItemId },
      {
        onSuccess: (chat) => {
          setSessionId(chat.sessionId);
          setHistory(chat.messages);
        },
      },
    );
  }, [opened, declarationId, goodsShipmentItemId]);

  const ask = (question: string) => {
    const key = nextKey.current++;
    setLocalQuestions((current) => [...current, { key, question, failed: false }]);
    sendMessage.mutate(
      { declarationId, goodsShipmentItemId, sessionId, question },
      {
        onSuccess: ({ message }) => {
          setLocalQuestions((current) => current.filter((local) => local.key !== key));
          setHistory((current) => (current.some((saved) => saved.id === message.id) ? current : [...current, message]));
        },
        onError: () => setLocalQuestions((current) => current.map((local) => (local.key === key ? { ...local, failed: true } : local))),
      },
    );
  };

  return {
    sessionId,
    history,
    localQuestions,
    ask,
    isPending: sendMessage.isPending,
    openError: openChat.isError && !sessionId ? openChat.error : undefined,
  };
};

const RiskAnalysisChatBody: React.FC<RiskAnalysisChatBodyProps & { opened: boolean }> = (props) => {
  const { sessionId, history, localQuestions, ask, isPending, openError } = useRiskAnalysisChat(props);
  const [question, setQuestion] = useState('');
  const viewport = useRef<HTMLDivElement>(null);

  useEffect(() => {
    viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' });
  }, [history, localQuestions, isPending]);

  const send = () => {
    const text = question.trim();
    if (!sessionId || !text || isPending) return;
    setQuestion('');
    ask(text);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  if (openError) {
    return (
      <Alert color="red" variant="light" title="The AI chat could not be opened" data-pw="ai-chat-start-error">
        {itemAiChatErrorMessage(openError)}
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Answers are generated by AI from the stored values of this goods item. They are advisory only and do not change the declaration.
      </Text>

      <ScrollArea h="min(640px, 65vh)" viewportRef={viewport} type="auto" offsetScrollbars>
        <Stack gap="sm" pr="xs">
          {!sessionId && (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Loading the conversation…
              </Text>
            </Group>
          )}
          {sessionId && history.length === 0 && localQuestions.length === 0 && (
            <Text size="sm" c="dimmed">
              Ask a question about this goods item, e.g. why its price or HS code was flagged. The conversation is shared with your colleagues.
            </Text>
          )}
          {history.map((message) => (
            <Stack key={message.id} gap="sm">
              <QuestionBubble question={message.question} author={authorLabel(message)} createdAt={message.createdAt} own={message.isCurrentUser} />
              <AnswerBubble answer={message.answer} />
            </Stack>
          ))}
          {localQuestions.map((local) => (
            <QuestionBubble key={`local-${local.key}`} question={local.question} author="You" own failed={local.failed} />
          ))}
          {isPending && (
            <Group gap="xs" data-pw="ai-chat-pending">
              <Loader size="xs" type="dots" />
              <Text size="sm" c="dimmed">
                Thinking… this can take up to a minute.
              </Text>
            </Group>
          )}
        </Stack>
      </ScrollArea>

      <Group align="flex-end" gap="xs" wrap="nowrap">
        <Textarea
          data-pw="ai-chat-input"
          style={{ flex: 1 }}
          placeholder="Ask about this goods item…"
          autosize
          minRows={1}
          maxRows={5}
          maxLength={MAX_QUESTION_LENGTH}
          value={question}
          disabled={!sessionId || isPending}
          onChange={(event) => setQuestion(event.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
        <Button data-pw="ai-chat-send-btn" onClick={send} loading={isPending} disabled={!sessionId || !question.trim()}>
          Send
        </Button>
      </Group>
    </Stack>
  );
};

export const RiskAnalysisChat: React.FC<RiskAnalysisChatBodyProps> = ({ declarationId, goodsShipmentItemId }) => {
  const itemKey = `${declarationId}-${goodsShipmentItemId}`;
  const [opened, setOpened] = useState(false);
  const [chatKey, setChatKey] = useState<string>();

  useEffect(() => {
    setOpened(false);
  }, [itemKey]);

  const toggle = () => {
    setChatKey(itemKey);
    setOpened((current) => !current);
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width="min(900px, 90vw)" shadow="md" withArrow keepMounted zIndex={1000}>
      <Popover.Target>
        <Button
          data-pw="ai-chat-btn"
          type="button"
          onClick={toggle}
          variant={opened ? 'filled' : 'light'}
          size="lg"
          rightSection={opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        >
          Ask AI
        </Button>
      </Popover.Target>
      <Popover.Dropdown data-pw="ai-chat-dropdown">
        <Group justify="space-between" mb="xs">
          <Text fw="bold">Ask AI about this goods item</Text>
          <CloseButton data-pw="ai-chat-close-btn" onClick={() => setOpened(false)} />
        </Group>
        {chatKey === itemKey && <RiskAnalysisChatBody key={chatKey} declarationId={declarationId} goodsShipmentItemId={goodsShipmentItemId} opened={opened} />}
      </Popover.Dropdown>
    </Popover>
  );
};
